/**
 * ReviewItemDetailPage — auditor-facing detail + resolution UI.
 *
 * Approve / Correct / Reject flows write back to the source row AND
 * the review-queue row, and move the document forward (parse_state
 * = parse_complete or failed). Once all docs reach terminal state,
 * the loan-file transitions to parsed | parsing_failed.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Edit3, X, AlertTriangle } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, ErrorState, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { parsingApi } from '@/api/parsing-api';
import {
  REVIEW_ITEM_TYPE,
  PARSING_CLASSIFIABLE_TYPES,
  type ClassificationDTO,
  type ExtractionDTO,
} from '@loanaudit/shared';

// Human-readable labels — underlying enum values stay for logic.
const TYPE_LABEL: Record<string, string> = {
  classification: 'Classification', extraction: 'Extraction',
};
const REVIEW_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending', approved: 'Approved', corrected: 'Corrected', rejected: 'Rejected',
};

export function ReviewItemDetailPage() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['parsing', 'reviewItem', reviewId],
    queryFn: () => parsingApi.getReviewItem(reviewId!),
    enabled: Boolean(reviewId),
  });

  const resolve = useMutation({
    mutationFn: (body: {
      action: 'approve' | 'correct' | 'reject';
      correctedType?: string;
      correctedFields?: Record<string, unknown>;
      notes?: string;
    }) => parsingApi.resolveReviewItem(reviewId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parsing', 'reviewQueue'] });
      qc.invalidateQueries({ queryKey: ['parsing', 'reviewItem', reviewId] });
      // Resolving writes the outcome onto the classification / extracted-fields
      // row, so the loan file's parsing checklist is now stale. It stops
      // polling once the file reaches a terminal state, which is exactly when
      // reviews get resolved — without these it would show the old state until
      // a manual reload.
      qc.invalidateQueries({ queryKey: ['parsing', 'loanFile'] });
      qc.invalidateQueries({ queryKey: ['loanFile'] });
      navigate('/parsing/review-queue');
    },
  });

  if (query.isLoading) {
    return (
      <AppShell title="Review">
        <Card>
          <div className="py-8 text-center text-sm text-text-muted">Loading…</div>
        </Card>
      </AppShell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <AppShell title="Review">
        <ErrorState
          title="Failed to load review item"
          description={query.error instanceof Error ? query.error.message : 'Unknown error'}
          action={
            <Button variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      </AppShell>
    );
  }

  const { review, source } = query.data;
  const isClassification = review.itemType === REVIEW_ITEM_TYPE.CLASSIFICATION;

  return (
    <AppShell title={`Review — ${review.loanNumber}`}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/parsing/review-queue" className="text-text-muted hover:text-text-primary inline-flex">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <PageHeader title={`Review — ${review.loanNumber}`} breadcrumbLabel={review.loanNumber} subtitle={review.documentFileName} />
        </div>

        <Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-label">
            <Cell label="Type"><Badge tone="info">{TYPE_LABEL[review.itemType] ?? review.itemType}</Badge></Cell>
            <Cell label="Status"><Badge tone="warning">{REVIEW_STATUS_LABEL[review.status] ?? review.status}</Badge></Cell>
            <Cell label="Confidence"><ConfidencePill value={review.confidence} /></Cell>
            <Cell label="Loan file">
              <Link
                to={`/loan-files/${review.loanFileId}`}
                className="text-brand-blue hover:underline font-medium"
              >
                {review.loanNumber}
              </Link>
            </Cell>
          </div>
        </Card>

        {isClassification ? (
          <ClassificationReviewPanel
            classification={source as ClassificationDTO | null}
            onResolve={(body) => resolve.mutate(body)}
            pending={resolve.isPending}
          />
        ) : (
          <ExtractionReviewPanel
            extraction={source as ExtractionDTO | null}
            onResolve={(body) => resolve.mutate(body)}
            pending={resolve.isPending}
          />
        )}
      </div>
    </AppShell>
  );
}

// =========================================================================

function ClassificationReviewPanel({
  classification,
  onResolve,
  pending,
}: {
  classification: ClassificationDTO | null;
  onResolve: (body: {
    action: 'approve' | 'correct' | 'reject';
    correctedType?: string;
    notes?: string;
  }) => void;
  pending: boolean;
}) {
  const [mode, setMode] = useState<'idle' | 'correct' | 'reject'>('idle');
  const [correctedType, setCorrectedType] = useState<string>(
    classification?.predictedType ?? 'other',
  );
  const [notes, setNotes] = useState('');

  if (!classification) {
    return (
      <Card>
        <div className="py-4 text-sm text-text-muted">Classification row not found.</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-3">
        <div className="text-body font-semibold text-text-primary">AI Classification</div>
      </div>
      <div className="text-label text-text-secondary">
        The classifier identified this document as
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-lg font-semibold text-text-primary">
          {classification.predictedType}
        </span>
        <ConfidencePill value={classification.confidence} />
      </div>
      <div className="mt-3 text-caption text-text-tertiary">
        Model: {classification.modelId} · Tokens: {classification.tokenInput ?? 0}↑/
        {classification.tokenOutput ?? 0}↓
      </div>

      <hr className="my-4 border-border" />

      {mode === 'idle' && (
        <div className="flex flex-wrap gap-2">
          <Button
            leftIcon={<Check className="w-4 h-4" />}
            onClick={() => onResolve({ action: 'approve' })}
            loading={pending}
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Edit3 className="w-4 h-4" />}
            onClick={() => setMode('correct')}
          >
            Correct type
          </Button>
          <Button
            variant="secondary"
            leftIcon={<X className="w-4 h-4" />}
            onClick={() => setMode('reject')}
          >
            Reject document
          </Button>
        </div>
      )}

      {mode === 'correct' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Correct document type</SectionLabel>
            <select
              className="mt-1 w-full md:w-80 border border-outline-variant rounded-lg px-3 py-2 text-label bg-surface-container-lowest outline-none focus:border-primary/50"
              value={correctedType}
              onChange={(e) => setCorrectedType(e.target.value)}
            >
              {PARSING_CLASSIFIABLE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <SectionLabel>Notes (optional)</SectionLabel>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why is this correction needed?"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() =>
                onResolve({ action: 'correct', correctedType, notes: notes || undefined })
              }
              loading={pending}
            >
              Save correction
            </Button>
            <Button variant="secondary" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'reject' && (
        <div className="space-y-3">
          <div className="text-label text-text-secondary flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
            Rejecting marks this document as failed. It will not be used by UC1/UC2/UC3.
          </div>
          <div>
            <SectionLabel>Reason</SectionLabel>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe why this document is unusable"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={() =>
                onResolve({ action: 'reject', notes: notes.trim() || 'No reason supplied' })
              }
              loading={pending}
              disabled={notes.trim().length === 0}
            >
              Reject document
            </Button>
            <Button variant="secondary" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// =========================================================================

function ExtractionReviewPanel({
  extraction,
  onResolve,
  pending,
}: {
  extraction: ExtractionDTO | null;
  onResolve: (body: {
    action: 'approve' | 'correct' | 'reject';
    correctedFields?: Record<string, unknown>;
    notes?: string;
  }) => void;
  pending: boolean;
}) {
  const [mode, setMode] = useState<'idle' | 'correct' | 'reject'>('idle');
  const [notes, setNotes] = useState('');
  const [corrections, setCorrections] = useState<Record<string, string>>({});

  if (!extraction) {
    return (
      <Card>
        <div className="py-4 text-sm text-text-muted">Extraction row not found.</div>
      </Card>
    );
  }

  const fields = (extraction.fields ?? {}) as Record<
    string,
    { value: unknown; confidence: number }
  >;
  const fieldEntries = Object.entries(fields);

  return (
    <Card>
      <div className="mb-3">
        <div className="text-body font-semibold text-text-primary">
          AI Extraction · {extraction.documentType}
        </div>
      </div>
      <div className="text-label text-text-secondary mb-3 flex items-center gap-2">
        Aggregate confidence: <ConfidencePill value={extraction.confidence} />
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-caption">
          <thead className="bg-surface-muted">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-text-secondary">Field</th>
              <th className="px-3 py-2 font-semibold text-text-secondary">AI value</th>
              <th className="px-3 py-2 font-semibold text-text-secondary w-24">Conf.</th>
              {mode === 'correct' && (
                <th className="px-3 py-2 font-semibold text-text-secondary">Correction</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {fieldEntries.length === 0 ? (
              <tr>
                <td
                  colSpan={mode === 'correct' ? 4 : 3}
                  className="px-3 py-4 text-center text-text-muted text-label"
                >
                  No extracted fields available. The extraction may not have
                  completed successfully — check the parsing logs.
                </td>
              </tr>
            ) : (
              fieldEntries.map(([key, v]) => (
              <tr key={key}>
                <td className="px-3 py-1.5 font-medium">{key}</td>
                <td className="px-3 py-1.5">{renderValue(v.value)}</td>
                <td className="px-3 py-1.5">
                  <ConfidencePill value={v.confidence} />
                </td>
                {mode === 'correct' && (
                  <td className="px-3 py-1.5">
                    <input
                      className="w-full border border-border rounded px-2 py-1 text-caption"
                      placeholder="Leave blank to keep"
                      value={corrections[key] ?? ''}
                      onChange={(e) =>
                        setCorrections((c) => ({ ...c, [key]: e.target.value }))
                      }
                    />
                  </td>
                )}
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-caption text-text-tertiary">
        Model: {extraction.modelId} · Tokens: {extraction.tokenInput ?? 0}↑/
        {extraction.tokenOutput ?? 0}↓ · Schema: {extraction.schemaId}@v{extraction.schemaVersion}
      </div>

      <hr className="my-4 border-border" />

      {mode === 'idle' && (
        <div className="flex flex-wrap gap-2">
          <Button
            leftIcon={<Check className="w-4 h-4" />}
            onClick={() => onResolve({ action: 'approve' })}
            loading={pending}
          >
            Approve as-is
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Edit3 className="w-4 h-4" />}
            onClick={() => setMode('correct')}
          >
            Correct fields
          </Button>
          <Button
            variant="secondary"
            leftIcon={<X className="w-4 h-4" />}
            onClick={() => setMode('reject')}
          >
            Reject document
          </Button>
        </div>
      )}

      {mode === 'correct' && (
        <div className="space-y-3">
          <div className="text-label text-text-secondary">
            Fill in values for any fields you want to correct. Leave blank to keep the AI value.
          </div>
          <div>
            <SectionLabel>Notes (optional)</SectionLabel>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                const nonEmpty = Object.fromEntries(
                  Object.entries(corrections).filter(([, v]) => v.trim().length > 0),
                );
                onResolve({
                  action: 'correct',
                  correctedFields: nonEmpty as Record<string, unknown>,
                  notes: notes || undefined,
                });
              }}
              loading={pending}
            >
              Save corrections
            </Button>
            <Button variant="secondary" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'reject' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Reason</SectionLabel>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={() =>
                onResolve({ action: 'reject', notes: notes.trim() || 'No reason supplied' })
              }
              loading={pending}
              disabled={notes.trim().length === 0}
            >
              Reject document
            </Button>
            <Button variant="secondary" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------------------

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map((x) => renderValue(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone: 'success' | 'warning' | 'danger' =
    value >= 0.85 ? 'success' : value >= 0.65 ? 'warning' : 'danger';
  return <Badge tone={tone}>{pct}%</Badge>;
}
